import { useTranslation } from 'react-i18next';
import * as stylex from '@stylexjs/stylex';
import { Button } from '@sentinel/ui';
import type { CredentialItem } from '../use-credentials';
import { sentinel } from '@sentinel/ui/theme/semantic.stylex';

const styles = stylex.create({
  section: { display: 'grid', gap: '0.75rem' },
  list: { display: 'grid', gap: '0.5rem', margin: 0, padding: 0, listStyle: 'none' },
  row: {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    alignItems: 'center',
    gap: '0.75rem',
    paddingTop: '0.75rem',
    paddingBottom: '0.75rem',
    paddingLeft: '1rem',
    paddingRight: '1rem',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: sentinel.borderDefault,
    borderRadius: '0.5rem',
    backgroundColor: `color-mix(in oklab, ${sentinel.surfaceElevated} 60%, transparent)`,
  },
  title: { fontWeight: 600 },
  meta: {
    fontSize: '0.8125rem',
    color: sentinel.textMuted,
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem 1rem',
    marginTop: '0.25rem',
  },
  badge: {
    display: 'inline-block',
    paddingTop: '0.125rem',
    paddingBottom: '0.125rem',
    paddingLeft: '0.5rem',
    paddingRight: '0.5rem',
    borderRadius: '999px',
    fontSize: '0.75rem',
    marginLeft: '0.5rem',
    backgroundColor: sentinel.borderDefault,
    color: sentinel.textMuted,
  },
  empty: { margin: 0, color: sentinel.textMuted },
});

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export interface CredentialsListProps {
  items: CredentialItem[];
  onRevoke: (cred: CredentialItem) => void;
  revokingId?: string | null;
}

export function CredentialsList({ items, onRevoke, revokingId }: CredentialsListProps) {
  const { t } = useTranslation('security');

  if (items.length === 0) {
    return (
      <p {...stylex.props(styles.empty)} data-testid="credentials-empty">
        {t('credentials_empty')}
      </p>
    );
  }

  return (
    <section {...stylex.props(styles.section)}>
      <ul {...stylex.props(styles.list)} data-testid="credentials-list">
        {items.map((c) => (
          <li key={c.id} {...stylex.props(styles.row)} data-testid={`credential-row-${c.id}`}>
            <div>
              <div {...stylex.props(styles.title)}>
                {c.name || t('credential_used_as_primary')}
                <span {...stylex.props(styles.badge)}>
                  {c.used_as === 'primary'
                    ? t('credential_used_as_primary')
                    : t('credential_used_as_mfa')}
                </span>
              </div>
              <div {...stylex.props(styles.meta)}>
                <span>
                  {t('credential_created_at_label')}: {formatDate(c.created_at)}
                </span>
                <span>
                  {t('credential_last_used_at_label')}:{' '}
                  {c.last_used_at ? formatDate(c.last_used_at) : t('credential_last_used_never')}
                </span>
              </div>
            </div>
            <Button
              type="button"
              variant="danger"
              size="sm"
              onClick={() => onRevoke(c)}
              disabled={revokingId === c.id}
              data-testid={`credential-revoke-${c.id}`}
            >
              {t('credential_revoke_button')}
            </Button>
          </li>
        ))}
      </ul>
    </section>
  );
}
